import React, { useState } from 'react';
import { Col, Row, Collapse } from 'reactstrap';
import styled from 'styled-components';
import { OutputRow, ButtonContainer, CollapseButton, BoldLabel, TableIcon } from '../../../../utils/budget-tool-specific';
import { RebelColors } from '../../../../styles/rebel-experiences';
import { displayValue } from '../../helper';
import { calculateContingency, calculateCostTrip, calculateCostMeals, calculateCostAccommodation, calculateCostActivity, calculateCostGroundTransport, calculateCostMeetings, calculateCostMiscellaneous, calculateCostFlight, calculateInternalCostA, calculateInternalCostB, calculateCostPerAttendee, calculateRebelContingency, calculateRebelSubtotal, calculateRebelTotalCost } from '../../experiences/cost-summary/cost-helper';
import TallyDisplay from '../components/tally-display';
import RankingDropdown from '../components/ranking-dropdown';


const RebelSummaryExperienceRow = ({
    guideData,
    expData,
    experienceId,
    client = false,
    allRankings = {},
    selectedRankings = {},
    onRankingChange,
    totalExperiences = 0,
    setIndex,
    clientCurrency
}) => {

    const normalStyle = {
        transition: '1s, transform 0.5s',
    };

    const transformedStyle = {
        transition: '1s, transform 0.5s',
        transform: 'rotate(180deg)',
    };

    const [isOpen, setIsOpen] = useState(false);

    // Calculate margin cost
    const calculateMarginCost = () => {
        if (guideData.marginPercent === undefined || guideData.marginPercent.value === true) {
            // Percentage-based margin (existing calculation)
            return calculateInternalCostB(guideData, expData, clientCurrency);
        } else {
            // Dollar-based margin - per person, multiplied by attendees
            const attendeesCount = parseFloat(guideData.attendees?.value) || 1;
            return parseFloat(guideData.margin.value) * attendeesCount;
        }
    };

    // Calculate total trip cost with corrected margin
    // For REBEL: use flat total cost (accommodation is not per-person)
    const calculateTotalTripCost = () => {
        const baseCost = calculateRebelTotalCost(guideData, expData);
        const overheadCost = calculateInternalCostA(guideData, expData, clientCurrency);
        const marginCost = calculateMarginCost();
        return baseCost + overheadCost + marginCost;
    };

    const attendees = parseFloat(guideData.attendees?.value) || 1;

    return (
        <React.Fragment>
            <UniformRow>
                <TallyCol lg="1" xs="1">
                    <ChevronButton onClick={() => {setIsOpen(!isOpen)}}>
                        <RebelCollapseButton icon={['fas', 'chevron-down']} style={isOpen ? transformedStyle : normalStyle}/>
                    </ChevronButton>
                    <TallyDisplay
                        allRankings={allRankings}
                        experienceId={experienceId}
                        displayMode="average"
                    />
                </TallyCol>
                <TextCol>
                    <RebelText>{expData.title.value}</RebelText>
                </TextCol>
                <TextCol lg="2">
                    <RebelText>
                        {guideData.currency.value.slice(3)}{displayValue(calculateTotalTripCost() / guideData.attendees.value)}
                    </RebelText>
                </TextCol>
                <TextCol lg="2">
                    <RebelText>
                        {guideData.currency.value.slice(3)}{displayValue(calculateTotalTripCost())}
                    </RebelText>
                </TextCol>
                {client && (
                    <RankingCol lg="1" xs="1">
                        <RankingDropdown
                            experienceId={experienceId}
                            selectedRankings={selectedRankings}
                            onChange={onRankingChange}
                            totalExperiences={totalExperiences}
                        />
                    </RankingCol>
                )}
                <Col lg="1">
                </Col>
            </UniformRow>
            <Collapse isOpen={isOpen}>
                <ExpandedContainer>
                    {/* Header Row */}
                    <ExpandedRow>
                        <Col lg="1" xs="1"></Col>
                        <CategoryCol>
                            <RebelBoldLabel>Categories</RebelBoldLabel>
                        </CategoryCol>
                        <PriceCol lg="2">
                            <RebelBoldLabel>Cost Per Attendee</RebelBoldLabel>
                        </PriceCol>
                        <PriceCol lg="2">
                            <RebelBoldLabel>Cost for Trip</RebelBoldLabel>
                        </PriceCol>
                        {client && <Col lg="1" xs="1"></Col>}
                        <Col lg="1"></Col>
                    </ExpandedRow>
                    {/* Accommodation - flat cost (not per person) */}
                    {calculateCostAccommodation(guideData, expData) > 0 && <ExpandedRow>
                        <Col lg="1" xs="1"></Col>
                        <CategoryCol>
                            <RebelText><RebelTableIcon icon={['fas', 'bed']} />Accommodation</RebelText>
                        </CategoryCol>
                        <PriceCol lg="2">
                            <RebelText>{guideData.currency.value.slice(3)}{displayValue(calculateCostAccommodation(guideData, expData) / attendees)}</RebelText>
                        </PriceCol>
                        <PriceCol lg="2">
                            <RebelText>{guideData.currency.value.slice(3)}{displayValue(calculateCostAccommodation(guideData, expData))}</RebelText>
                        </PriceCol>
                        {client && <Col lg="1" xs="1"></Col>}
                        <Col lg="1"></Col>
                    </ExpandedRow>}
                    {/* Flights */}
                    {calculateCostFlight(guideData, expData) > 0 && <ExpandedRow>
                        <Col lg="1" xs="1"></Col>
                        <CategoryCol>
                            <RebelText><RebelTableIcon icon={['fas', 'plane']} />Flights</RebelText>
                        </CategoryCol>
                        <PriceCol lg="2">
                            <RebelText>{guideData.currency.value.slice(3)}{displayValue(calculateCostFlight(guideData, expData))}</RebelText>
                        </PriceCol>
                        <PriceCol lg="2">
                            <RebelText>{guideData.currency.value.slice(3)}{displayValue(calculateCostFlight(guideData, expData) * guideData.attendees.value)}</RebelText>
                        </PriceCol>
                        {client && <Col lg="1" xs="1"></Col>}
                        <Col lg="1"></Col>
                    </ExpandedRow>}
                    {/* Activities */}
                    {calculateCostActivity(guideData, expData) > 0 && <ExpandedRow>
                        <Col lg="1" xs="1"></Col>
                        <CategoryCol>
                            <RebelText><RebelTableIcon icon={['fas', 'sticky-note']} />Activities</RebelText>
                        </CategoryCol>
                        <PriceCol lg="2">
                            <RebelText>{guideData.currency.value.slice(3)}{displayValue(calculateCostActivity(guideData, expData))}</RebelText>
                        </PriceCol>
                        <PriceCol lg="2">
                            <RebelText>{guideData.currency.value.slice(3)}{displayValue(calculateCostActivity(guideData, expData) * guideData.attendees.value)}</RebelText>
                        </PriceCol>
                        {client && <Col lg="1" xs="1"></Col>}
                        <Col lg="1"></Col>
                    </ExpandedRow>}
                    {/* Ground Transportation */}
                    {calculateCostGroundTransport(guideData, expData) > 0 && <ExpandedRow>
                        <Col lg="1" xs="1"></Col>
                        <CategoryCol>
                            <RebelText><RebelTableIcon icon={['fas', 'car']} />Ground Transportation</RebelText>
                        </CategoryCol>
                        <PriceCol lg="2">
                            <RebelText>{guideData.currency.value.slice(3)}{displayValue(calculateCostGroundTransport(guideData, expData))}</RebelText>
                        </PriceCol>
                        <PriceCol lg="2">
                            <RebelText>{guideData.currency.value.slice(3)}{displayValue(calculateCostGroundTransport(guideData, expData) * guideData.attendees.value)}</RebelText>
                        </PriceCol>
                        {client && <Col lg="1" xs="1"></Col>}
                        <Col lg="1"></Col>
                    </ExpandedRow>}
                    {/* Meals */}
                    {calculateCostMeals(guideData, expData) > 0 && <ExpandedRow>
                        <Col lg="1" xs="1"></Col>
                        <CategoryCol>
                            <RebelText><RebelTableIcon icon={['fas', 'utensils']} />Meals</RebelText>
                        </CategoryCol>
                        <PriceCol lg="2">
                            <RebelText>{guideData.currency.value.slice(3)}{displayValue(calculateCostMeals(guideData, expData))}</RebelText>
                        </PriceCol>
                        <PriceCol lg="2">
                            <RebelText>{guideData.currency.value.slice(3)}{displayValue(calculateCostMeals(guideData, expData) * guideData.attendees.value)}</RebelText>
                        </PriceCol>
                        {client && <Col lg="1" xs="1"></Col>}
                        <Col lg="1"></Col>
                    </ExpandedRow>}
                    {/* Meetings */}
                    {calculateCostMeetings(guideData, expData) > 0 && <ExpandedRow>
                        <Col lg="1" xs="1"></Col>
                        <CategoryCol>
                            <RebelText><RebelTableIcon icon={['fas', 'table']} />Meetings</RebelText>
                        </CategoryCol>
                        <PriceCol lg="2">
                            <RebelText>{guideData.currency.value.slice(3)}{displayValue(calculateCostMeetings(guideData, expData))}</RebelText>
                        </PriceCol>
                        <PriceCol lg="2">
                            <RebelText>{guideData.currency.value.slice(3)}{displayValue(calculateCostMeetings(guideData, expData) * guideData.attendees.value)}</RebelText>
                        </PriceCol>
                        {client && <Col lg="1" xs="1"></Col>}
                        <Col lg="1"></Col>
                    </ExpandedRow>}
                    {/* Miscellaneous */}
                    {calculateCostMiscellaneous(guideData, expData) > 0 && <ExpandedRow>
                        <Col lg="1" xs="1"></Col>
                        <CategoryCol>
                            <RebelText><RebelTableIcon icon={['fas', 'plus']} />Miscellaneous Costs</RebelText>
                        </CategoryCol>
                        <PriceCol lg="2">
                            <RebelText>{guideData.currency.value.slice(3)}{displayValue(calculateCostMiscellaneous(guideData, expData))}</RebelText>
                        </PriceCol>
                        <PriceCol lg="2">
                            <RebelText>{guideData.currency.value.slice(3)}{displayValue(calculateCostMiscellaneous(guideData, expData) * guideData.attendees.value)}</RebelText>
                        </PriceCol>
                        {client && <Col lg="1" xs="1"></Col>}
                        <Col lg="1"></Col>
                    </ExpandedRow>}
                    {/* Contingency - flat % of total costs (not per person) */}
                    {calculateRebelContingency(guideData, expData) > 0 && <ExpandedRow>
                        <Col lg="1" xs="1"></Col>
                        <CategoryCol>
                            <RebelText><RebelTableIcon icon={['fas', 'shield-alt']} />Contingency ({guideData.contingency.value}%)</RebelText>
                        </CategoryCol>
                        <PriceCol lg="2">
                            <RebelText>{guideData.currency.value.slice(3)}{displayValue(calculateRebelContingency(guideData, expData) / attendees)}</RebelText>
                        </PriceCol>
                        <PriceCol lg="2">
                            <RebelText>{guideData.currency.value.slice(3)}{displayValue(calculateRebelContingency(guideData, expData))}</RebelText>
                        </PriceCol>
                        {client && <Col lg="1" xs="1"></Col>}
                        <Col lg="1"></Col>
                    </ExpandedRow>}
                    {/* Separator */}
                    <ExpandedRow>
                        <Col lg="1" xs="1"></Col>
                        <Col><SeparatorLine /></Col>
                        {client && <Col lg="1" xs="1"></Col>}
                        <Col lg="1"></Col>
                    </ExpandedRow>
                    {/* Subtotal - uses REBEL calculation with flat accommodation */}
                    <ExpandedRow>
                        <Col lg="1" xs="1"></Col>
                        <CategoryCol>
                            <RebelBoldLabel>Subtotal</RebelBoldLabel>
                        </CategoryCol>
                        <PriceCol lg="2">
                            <RebelBoldLabel>{guideData.currency.value.slice(3)}{displayValue(calculateRebelTotalCost(guideData, expData) / attendees)}</RebelBoldLabel>
                        </PriceCol>
                        <PriceCol lg="2">
                            <RebelBoldLabel>{guideData.currency.value.slice(3)}{displayValue(calculateRebelTotalCost(guideData, expData))}</RebelBoldLabel>
                        </PriceCol>
                        {client && <Col lg="1" xs="1"></Col>}
                        <Col lg="1"></Col>
                    </ExpandedRow>
                    {/* Admin Fee */}
                    <ExpandedRow>
                        <Col lg="1" xs="1"></Col>
                        <CategoryCol>
                            <RebelText>
                                {(guideData.overheadPercent === undefined || guideData.overheadPercent.value === true)
                                    ? `Administrative & Management Fee (${guideData.overhead.value}%)`
                                    : "Administrative & Management Fee"}
                            </RebelText>
                        </CategoryCol>
                        <PriceCol lg="2"></PriceCol>
                        <PriceCol lg="2">
                            <RebelText>{guideData.currency.value.slice(3)}{displayValue(calculateInternalCostA(guideData, expData))}</RebelText>
                        </PriceCol>
                        {client && <Col lg="1" xs="1"></Col>}
                        <Col lg="1"></Col>
                    </ExpandedRow>
                    {/* Planning Fee */}
                    {guideData.margin.value !== "0" && <ExpandedRow>
                        <Col lg="1" xs="1"></Col>
                        <CategoryCol>
                            <RebelText>
                                {(guideData.marginPercent === undefined || guideData.marginPercent.value === true)
                                    ? `Planning Fee (${guideData.margin.value}%)`
                                    : `Planning Fee (${guideData.currency.value.slice(3)}${displayValue(parseFloat(guideData.margin.value))}/pp x ${attendees})`}
                            </RebelText>
                        </CategoryCol>
                        <PriceCol lg="2"></PriceCol>
                        <PriceCol lg="2">
                            <RebelText>{guideData.currency.value.slice(3)}{displayValue(calculateMarginCost())}</RebelText>
                        </PriceCol>
                        {client && <Col lg="1" xs="1"></Col>}
                        <Col lg="1"></Col>
                    </ExpandedRow>}
                    {/* Separator */}
                    <ExpandedRow>
                        <Col lg="1" xs="1"></Col>
                        <Col><SeparatorLine /></Col>
                        {client && <Col lg="1" xs="1"></Col>}
                        <Col lg="1"></Col>
                    </ExpandedRow>
                    {/* Total */}
                    <ExpandedRow>
                        <Col lg="1" xs="1"></Col>
                        <CategoryCol>
                            <RebelBoldLabel style={{color: RebelColors.gold}}>Total Program Cost</RebelBoldLabel>
                        </CategoryCol>
                        <PriceCol lg="2">
                            <RebelBoldLabel style={{color: RebelColors.gold}}>{guideData.currency.value.slice(3)}{displayValue(calculateTotalTripCost() / guideData.attendees.value)}</RebelBoldLabel>
                        </PriceCol>
                        <PriceCol lg="2">
                            <RebelBoldLabel style={{color: RebelColors.gold}}>{guideData.currency.value.slice(3)}{displayValue(calculateTotalTripCost())}</RebelBoldLabel>
                        </PriceCol>
                        {client && <Col lg="1" xs="1"></Col>}
                        <Col lg="1"></Col>
                    </ExpandedRow>
                </ExpandedContainer>
            </Collapse>
        </React.Fragment>
    )
}

export default RebelSummaryExperienceRow;

const ExpandedContainer = styled.div`
    padding: 8px 0;
`

const RebelCollapseButton = styled(CollapseButton)`
    color: ${RebelColors.gold};
`

const RebelTableIcon = styled(TableIcon)`
    color: ${RebelColors.gold};
    margin-right: 8px;
    width: 16px !important;
`

const RebelBoldLabel = styled(BoldLabel)`
    color: ${RebelColors.gold};
`

const SeparatorLine = styled.div`
    background-color: ${RebelColors.gold};
    height: 2px;
    margin: 8px 0;
    border-radius: 1px;
`

const UniformRow = styled(OutputRow)`
    display: flex;
    align-items: center;
    padding: 4px 0;
`;

const RebelText = styled.p`
    color: ${RebelColors.text};
    margin: 0;
    padding: 0;
`

const RebelButtonContainer = styled(ButtonContainer)`
    &:hover {
        background-color: #000000;
    }
`

const TallyCol = styled(Col)`
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 4px;
    padding-left: 5px !important;
    padding-right: 5px !important;
`;

const TextCol = styled(Col)`
    display: flex;
    align-items: center;
`;

const RankingCol = styled(Col)`
    display: flex;
    align-items: center;
    justify-content: center;
`;

const ChevronButton = styled.div`
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 6px;
    border-radius: 4px;
    transition: background-color 0.3s ease;
    height: 32px;
    width: 32px;

    &:hover {
        background-color: #000000;
    }
`

const ExpandedRow = styled(Row)`
    display: flex;
    align-items: center;
    padding: 2px 0;
`;

const CategoryCol = styled(Col)`
    flex: 1;
`;

const PriceCol = styled(Col)`
    display: flex;
    align-items: center;
`;
